export type ConsentGateReason = 'missing' | 'revoked' | null;

export interface ConsentGateState {
  accessBlocked: boolean;
  reason: ConsentGateReason;
  returnTo: string | null;
  updatedAt: string;
}

export const CONSENT_REQUIRED_ROUTE = '/auth/consent-required';
export const CONSENT_GATE_STORAGE_KEY = 'caloriescience_consent_gate';

const isBrowser = () => typeof window !== 'undefined';

export const readConsentGateState = (): ConsentGateState | null => {
  if (!isBrowser()) {
    return null;
  }

  try {
    const raw = localStorage.getItem(CONSENT_GATE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<ConsentGateState>;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return {
      accessBlocked: Boolean(parsed.accessBlocked),
      reason:
        parsed.reason === 'missing' || parsed.reason === 'revoked'
          ? parsed.reason
          : null,
      returnTo: typeof parsed.returnTo === 'string' ? parsed.returnTo : null,
      updatedAt:
        typeof parsed.updatedAt === 'string'
          ? parsed.updatedAt
          : new Date().toISOString(),
    };
  } catch {
    return null;
  }
};

export const persistConsentGateState = (
  input: Partial<ConsentGateState> & { accessBlocked: boolean }
) => {
  if (!isBrowser()) {
    return;
  }

  const previousState = readConsentGateState();
  const nextState: ConsentGateState = {
    accessBlocked: input.accessBlocked,
    reason:
      input.reason === 'missing' || input.reason === 'revoked'
        ? input.reason
        : previousState?.reason ?? null,
    returnTo:
      typeof input.returnTo === 'string'
        ? input.returnTo
        : previousState?.returnTo ?? null,
    updatedAt: new Date().toISOString(),
  };

  localStorage.setItem(CONSENT_GATE_STORAGE_KEY, JSON.stringify(nextState));
};

export const clearConsentGateState = () => {
  if (!isBrowser()) {
    return;
  }
  localStorage.removeItem(CONSENT_GATE_STORAGE_KEY);
};

const normalizeReturnPath = (path: string | null | undefined): string | null => {
  if (!path || typeof path !== 'string') {
    return null;
  }
  const trimmed = path.trim();
  if (!trimmed.startsWith('/')) {
    return null;
  }
  return trimmed;
};

const getCurrentRelativePath = (): string => {
  if (!isBrowser()) {
    return '/dashboard';
  }
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

export const resolveConsentReturnPath = (fallback: string = '/dashboard'): string => {
  const fromState = normalizeReturnPath(readConsentGateState()?.returnTo);
  return fromState || fallback;
};

export const redirectToConsentGate = (params?: {
  reason?: ConsentGateReason;
  returnTo?: string | null;
}) => {
  if (!isBrowser()) {
    return;
  }

  const returnTo = normalizeReturnPath(params?.returnTo) || getCurrentRelativePath();
  const reason =
    params?.reason === 'missing' || params?.reason === 'revoked'
      ? params.reason
      : null;

  persistConsentGateState({
    accessBlocked: true,
    reason,
    returnTo,
  });

  const encodedReturnTo = encodeURIComponent(returnTo);
  if (window.location.pathname !== CONSENT_REQUIRED_ROUTE) {
    window.location.href = `${CONSENT_REQUIRED_ROUTE}?from=${encodedReturnTo}`;
  }
};

